import { DateTime } from 'luxon';

function getCandleTimeStamp(candle) {
  if (Number.isFinite(Number(candle?.timeStamp))) {
    return Number(candle.timeStamp);
  }

  if (candle?.time) {
    const parsed = DateTime.fromISO(candle.time, { setZone: true });
    if (parsed.isValid) {
      return parsed.toMillis();
    }
  }

  return null;
}

function floorToDuration(value, duration) {
  return value - ((value % duration) + duration) % duration;
}

export function isFuturesFamilySymbol(symbol) {
  return typeof symbol === 'string' && (symbol.startsWith('/') || symbol.startsWith('./'));
}

function normalizeSessionWindow(sessionWindow) {
  const startTimeStamp = Number(sessionWindow?.startTimeStamp);
  const endTimeStamp = Number(sessionWindow?.endTimeStamp);
  const sessionStartTimeStamp = Number(
    sessionWindow?.sessionStartTimeStamp ?? startTimeStamp
  );
  const sessionEndTimeStamp = Number(
    sessionWindow?.sessionEndTimeStamp ?? endTimeStamp
  );

  if (
    !Number.isFinite(startTimeStamp) ||
    !Number.isFinite(endTimeStamp) ||
    !Number.isFinite(sessionStartTimeStamp) ||
    !Number.isFinite(sessionEndTimeStamp)
  ) {
    return null;
  }

  return {
    ...sessionWindow,
    startTimeStamp,
    endTimeStamp,
    sessionStartTimeStamp,
    sessionEndTimeStamp,
  };
}

function resolveMatchingSessionWindow(timeStamp, sessionWindows) {
  if (!Array.isArray(sessionWindows) || sessionWindows.length === 0) {
    return null;
  }

  return (
    sessionWindows
    .map(normalizeSessionWindow)
    .filter(Boolean)
    .find(
      (sessionWindow) =>
        timeStamp >= sessionWindow.sessionStartTimeStamp &&
        timeStamp < sessionWindow.sessionEndTimeStamp
    ) ?? null
  );
}

function getDefaultEquityAnchor(dateTime) {
  return dateTime.set({
    hour: 9,
    minute: 30,
    second: 0,
    millisecond: 0,
  });
}

export function buildMinuteAnchorContext({
  candles,
  symbol,
  sessionWindows,
  referenceTimeStamp = null,
}) {
  if (!Array.isArray(candles) || candles.length === 0) {
    return null;
  }

  if (isFuturesFamilySymbol(symbol)) {
    const normalizedWindows = Array.isArray(sessionWindows)
      ? sessionWindows.map(normalizeSessionWindow).filter(Boolean)
      : [];
    if (normalizedWindows.length === 0) {
      return null;
    }

    const candleTimeStamps = candles
      .map(getCandleTimeStamp)
      .filter((timeStamp) => Number.isFinite(timeStamp));
    if (candleTimeStamps.length === 0) {
      return null;
    }

    const visibleStart = Math.min(...candleTimeStamps);
    const visibleEnd = Math.max(...candleTimeStamps);
    const referenceMillis = Number.isFinite(Number(referenceTimeStamp))
      ? Number(referenceTimeStamp)
      : DateTime.now().setZone('America/New_York').toMillis();

    const matchingWindow =
      normalizedWindows.find(
        (sessionWindow) =>
          visibleStart < sessionWindow.sessionEndTimeStamp &&
          visibleEnd >= sessionWindow.sessionStartTimeStamp
      ) ??
      resolveMatchingSessionWindow(referenceMillis, normalizedWindows) ??
      null;

    if (!matchingWindow) {
      return null;
    }

    const rthAnchor = DateTime.fromMillis(matchingWindow.startTimeStamp, {
      zone: 'America/New_York',
    });
    const sessionAnchor = DateTime.fromMillis(
      matchingWindow.sessionStartTimeStamp,
      {
        zone: 'America/New_York',
      }
    );
    const cutoverAnchor = rthAnchor.set({
      hour: 4,
      minute: 0,
      second: 0,
      millisecond: 0,
    });

    return {
      anchor: referenceMillis < cutoverAnchor.toMillis() ? sessionAnchor : rthAnchor,
    };
  }

  const firstTimeStamp = candles
    .map(getCandleTimeStamp)
    .find((timeStamp) => Number.isFinite(timeStamp));
  if (!Number.isFinite(firstTimeStamp)) {
    return null;
  }

  return {
    anchor: getDefaultEquityAnchor(
      DateTime.fromMillis(firstTimeStamp, { zone: 'America/New_York' })
    ),
  };
}

function normalizeTimeUnitValue(value) {
  if (value == null) {
    return '';
  }

  return String(value).trim().toUpperCase();
}

function normalizeDurationValue(value) {
  const normalized = Number(value);
  if (!Number.isFinite(normalized) || normalized <= 0) {
    return null;
  }

  return normalized;
}

function candleAlreadyMatchesRequestedTimeframe(
  candle,
  normalizedTimeUnit,
  normalizedDuration
) {
  const candleTimeUnit = normalizeTimeUnitValue(candle?.timeUnit);
  const candleDuration = normalizeDurationValue(
    candle?.duration ?? candle?.candleDuration
  );

  return (
    candleTimeUnit === normalizedTimeUnit &&
    candleDuration === normalizedDuration
  );
}

function shouldPreserveProviderBucket(
  candle,
  normalizedTimeUnit,
  normalizedDuration,
  sessionWindows
) {
  if (
    !candleAlreadyMatchesRequestedTimeframe(
      candle,
      normalizedTimeUnit,
      normalizedDuration
    )
  ) {
    return false;
  }

  if (
    normalizedTimeUnit === 'MINUTES' &&
    isFuturesFamilySymbol(candle?.symbol) &&
    Array.isArray(sessionWindows) &&
    sessionWindows.length > 0
  ) {
    return false;
  }

  return true;
}

/**
 * Floors a raw timestamp to the start of its candle bucket for the given timeUnit/duration,
 * anchored to `minuteAnchorContext` for MINUTES buckets (9:30 ET for equities, the relevant
 * session start for futures — see `buildMinuteAnchorContext`).
 *
 * Exported (in addition to the internal `bucketCandleTimeStamp` below) so callers that bucket
 * raw, never-pre-bucketed timestamps — e.g. ChartCandleStore deriving a candle bucket from a
 * live trade/time-and-sale tick in `_chartCandleStore.js` — can floor a 1-minute-duration
 * timestamp too. `bucketCandleTimeStamp`'s `duration <= 1` shortcut below assumes the backend
 * already pre-bucketed 1-minute provider candles, which is true for `getChart`/`stream/candle`
 * payloads but never true for a raw trade tick.
 */
export function floorTimeStampToBucket(
  timeStamp,
  timeUnit,
  candleDuration,
  minuteAnchorContext = null
) {
  const normalizedTimeUnit = normalizeTimeUnitValue(timeUnit);
  const normalizedDuration = normalizeDurationValue(candleDuration) ?? 1;
  const dateTime = DateTime.fromMillis(timeStamp, { zone: 'America/New_York' });
  if (!dateTime.isValid) {
    return timeStamp;
  }

  switch (normalizedTimeUnit) {
    case 'MINUTES': {
      const sessionAnchor = minuteAnchorContext?.anchor;
      const anchor = sessionAnchor ?? dateTime.startOf('day');
      const minutesSinceAnchor = Math.floor(
        dateTime.diff(anchor, 'minutes').minutes
      );
      return anchor
        .plus({
          minutes: floorToDuration(minutesSinceAnchor, normalizedDuration),
        })
        .toMillis();
    }
    case 'HOURS':
      return dateTime
        .startOf('day')
        .plus({
          hours: floorToDuration(dateTime.hour, normalizedDuration),
        })
        .toMillis();
    case 'DAYS':
      return dateTime
        .startOf('day')
        .plus({
          days: floorToDuration(dateTime.ordinal - 1, normalizedDuration),
        })
        .toMillis();
    case 'WEEKS': {
      const weekStart = dateTime.startOf('week');
      const weeksSinceYearStart = Math.floor(
        weekStart.diff(weekStart.startOf('year').startOf('week'), 'weeks').weeks
      );
      return weekStart
        .startOf('year')
        .startOf('week')
        .plus({
          weeks: floorToDuration(weeksSinceYearStart, normalizedDuration),
        })
        .toMillis();
    }
    case 'MONTHS':
      return dateTime
        .startOf('year')
        .plus({
          months: floorToDuration(dateTime.month - 1, normalizedDuration),
        })
        .toMillis();
    default:
      return timeStamp;
  }
}

function bucketCandleTimeStamp(
  candle,
  timeUnit,
  candleDuration,
  sessionWindows,
  minuteAnchorContext = null
) {
  const timeStamp = getCandleTimeStamp(candle);
  if (!Number.isFinite(timeStamp)) {
    return null;
  }

  const normalizedTimeUnit = normalizeTimeUnitValue(timeUnit);
  const normalizedDuration = normalizeDurationValue(candleDuration);
  if (!Number.isFinite(normalizedDuration) || normalizedDuration <= 1) {
    return timeStamp;
  }

  if (
    shouldPreserveProviderBucket(
      candle,
      normalizedTimeUnit,
      normalizedDuration,
      sessionWindows
    )
  ) {
    return timeStamp;
  }

  // When a futures MINUTES candle has no session anchor (empty `sessionWindows`),
  // fall through to `floorTimeStampToBucket` rather than returning the raw timestamp.
  // With a null anchor it floors to a midnight-ET grid (`dateTime.startOf('day')`),
  // which aligns with every current futures open (:00/:20/:30) for durations that
  // divide evenly from midnight (<= 30m). For 60m+ the midnight grid is :00-aligned
  // and therefore mis-anchored vs the 9:30 equity-index open — a known limitation of
  // the no-window fallback (correct hourly alignment requires `sessionWindows`) — but
  // a single mis-anchored bar is still far better than per-tick degenerate output.
  // Net: never emit raw, unbucketed futures minute candles; always floor to some grid.
  return floorTimeStampToBucket(
    timeStamp,
    timeUnit,
    candleDuration,
    minuteAnchorContext
  );
}

export function normalizeChartCandles({
  candles,
  timeUnit = null,
  candleDuration = null,
  sessionWindows = [],
  referenceTimeStamp = null,
}) {
  if (!Array.isArray(candles) || candles.length === 0) {
    return [];
  }

  const minuteAnchorContext =
    normalizeTimeUnitValue(timeUnit) === 'MINUTES'
      ? buildMinuteAnchorContext({
          candles,
          symbol: candles[0]?.symbol,
          sessionWindows,
          referenceTimeStamp,
        })
      : null;

  const sortedCandles = [...candles]
    .map((candle) => ({
      ...candle,
      liveSourceTimeStamp: getCandleTimeStamp(candle),
      timeStamp: bucketCandleTimeStamp(
        candle,
        timeUnit,
        candleDuration,
        sessionWindows,
        minuteAnchorContext
      ),
    }))
    .filter((candle) => candle.timeStamp !== null)
    .sort((left, right) => left.timeStamp - right.timeStamp);

  const buckets = new Map();

  sortedCandles.forEach((candle) => {
    const existing = buckets.get(candle.timeStamp);
    const candleVolume = Number(candle.volume ?? 0);
    const candleOpen = Number(candle.open);
    const candleHigh = Number(candle.high);
    const candleLow = Number(candle.low);
    const candleClose = Number(candle.close);

    if (!existing) {
      buckets.set(candle.timeStamp, {
        ...candle,
        open: candleOpen,
        high: candleHigh,
        low: candleLow,
        close: candleClose,
        volume: candleVolume,
      });
      return;
    }

    existing.high = Math.max(existing.high, candleHigh);
    existing.low = Math.min(existing.low, candleLow);
    existing.close = candleClose;
    existing.volume += candleVolume;
    existing.completeCandle = Boolean(existing.completeCandle && candle.completeCandle);
    existing.red = existing.close < existing.open;
    existing.green = existing.close > existing.open;
  });

  return [...buckets.values()].sort((left, right) => left.timeStamp - right.timeStamp);
}
